import {
  AMOUNT_OF_BI_WEEKLY_DAYS,
  PAY_RUN_PREVIEW_AMOUNT_OF_BI_WEEKLY_DAYS,
  PAY_RUN_PREVIEW_AMOUNT_OF_MONTHLY_DAYS,
  PAY_RUN_PREVIEW_WEEKLY_DAYS,
} from "../constants"
import type { PaymentTermsSchemaFormValues } from "../schemas/PaymentTermsSchema"

import type {
  Maybe,
  PayRunSchedule,
  PaymentOptions,
  PaymentTerm,
  PaymentTermCreateInput,
} from "@/graphql/codegen/graphql"
import { MaturityDateTypeGql, PayPatternGql, PaymentRuleEventGql } from "@/graphql/codegen/graphql"

export const serializePaymentTermsForm = (data: PaymentTermsSchemaFormValues): PaymentTermCreateInput => {
  return {
    name: data.name,
    options: data.options.map((option) => ({
      parts: option.parts.map((part) => ({
        paymentRule: {
          daysAfter: parseInt(part.paymentRule.daysAfter),
          event: part.paymentRule.event,
          percentage: parseFloat(part.paymentRule.percentage),
        },
        ...(part.includeSalesDiscount && part.discount.percentage && part.discount.applicableIfWithinDays
          ? {
              discount: {
                percentage: parseFloat(part.discount.percentage),
                applicableIfWithinDays: parseInt(part.discount.applicableIfWithinDays),
              },
            }
          : {}),
      })),
    })),
    ...(data.payRunSchedule.isEnabled &&
    data.payRunSchedule.payPattern &&
    data.payRunSchedule.startDate &&
    data.payRunSchedule.payDays &&
    data.payRunSchedule.invoiceMaturity.intervalBetweenPayRuns
      ? {
          payRunSchedule: {
            payPattern: data.payRunSchedule.payPattern,
            startDate: data.payRunSchedule.startDate,
            payDays: data.payRunSchedule.payDays
              .split(",")
              .map((d) => parseInt(d.trim()))
              .filter(Number),
            invoiceMaturity: {
              intervalBetweenPayRuns: data.payRunSchedule.invoiceMaturity.intervalBetweenPayRuns,
              offsetInDays: data.payRunSchedule.invoiceMaturity.offsetInDays
                ? parseInt(data.payRunSchedule.invoiceMaturity.offsetInDays)
                : undefined,
            },
          },
        }
      : {}),
  }
}
export const buildDefaultFormValues = (data?: PaymentTerm): PaymentTermsSchemaFormValues => {
  const defaultDiscount = {
    percentage: "",
    applicableIfWithinDays: "",
  }

  const defaultPart = {
    paymentRule: {
      daysAfter: "",
      event: PaymentRuleEventGql.PlacingOrder,
      percentage: "",
    },
    includeSalesDiscount: false,
    discount: defaultDiscount,
  }

  const convertOptionData = (options: PaymentOptions[] = []) =>
    options.map(({ parts }) => ({
      parts: parts.map(({ paymentRule, discount }) => ({
        paymentRule: {
          daysAfter: String(paymentRule.daysAfter ?? ""),
          event: paymentRule.event,
          percentage: String(paymentRule.percentage ?? ""),
        },
        includeSalesDiscount: Boolean(discount),
        discount: {
          percentage: String(discount?.percentage ?? ""),
          applicableIfWithinDays: String(discount?.applicableIfWithinDays ?? ""),
        },
      })),
    }))

  const convertPayRunData = (
    payRunSchedule?: Maybe<PayRunSchedule>
  ): PaymentTermsSchemaFormValues["payRunSchedule"] => ({
    isEnabled: Boolean(payRunSchedule),
    payPattern: payRunSchedule?.payPattern ?? PayPatternGql.Weekly,
    startDate: payRunSchedule?.startDate ?? "",
    payDays: payRunSchedule?.payDays?.join(",") ?? "",
    invoiceMaturity: {
      intervalBetweenPayRuns:
        payRunSchedule?.invoiceMaturity?.intervalBetweenPayRuns ?? MaturityDateTypeGql.PayInterval,
      offsetInDays: String(payRunSchedule?.invoiceMaturity?.offsetInDays ?? ""),
    },
  })

  return {
    name: data?.name ?? "",
    options: data?.options?.length ? convertOptionData(data.options) : [{ parts: [defaultPart] }],
    payRunSchedule: convertPayRunData(data?.payRunSchedule),
  }
}

export const getPayRunPreviewItems = (payPattern: PayPatternGql): string[] => {
  if (payPattern === PayPatternGql.Monthly) {
    return Array.from({ length: PAY_RUN_PREVIEW_AMOUNT_OF_MONTHLY_DAYS }, (_, i) => String(i + 1))
  }

  if (payPattern === PayPatternGql.BiWeekly) {
    return Array.from(
      { length: PAY_RUN_PREVIEW_AMOUNT_OF_BI_WEEKLY_DAYS },
      (_, i) => PAY_RUN_PREVIEW_WEEKLY_DAYS[i % 7]
    )
  }
  return PAY_RUN_PREVIEW_WEEKLY_DAYS
}

export const getPayRunPreviewSelectedPayDays = (payPattern: PayPatternGql, payDays?: string): number[] => {
  if (payDays) {
    const parsedPayDays = payDays
      .split(",")
      .map((d) => parseInt(d.trim()))
      .filter(Number)

    if (payPattern === PayPatternGql.BiWeekly) {
      // If we have pay days with a value of 5, we need to highlight FR in the 1st week and 3rd week
      return parsedPayDays.flatMap((d) => [d, d + AMOUNT_OF_BI_WEEKLY_DAYS])
    }

    return parsedPayDays
  }
  return []
}

export const getPayRunSchedulePayDayTooltipMessage = (pattern: PayPatternGql): string => {
  if (pattern === PayPatternGql.Monthly) {
    return "[#] = [Day of the Month]"
  }
  if (pattern === PayPatternGql.BiWeekly) {
    return "[#] = [Day of the Week] \n Ex: 1=Monday, 2=Tuesday, 8=Week 2, Monday, 9=Week 2, Tuesday"
  }
  return "[#] = [Day of the Week] \n Ex: 1=Monday, 2=Tuesday, etc"
}
